import { useContext, createContext, useMemo, useCallback } from 'react';

const PermissionsContext = createContext(null);

// Define permission prerequisites - if you have X, you automatically have Y
const PERMISSION_PREREQUISITES = {
  'view_wages_history': ['view_wages'],
  'edit_wages': ['view_wages'],
  'edit_employees': ['view_employees'],
  'view_terminated_employees': ['view_employees'],
  'edit_employee_name': ['view_employees'],
  'edit_employee_email': ['view_employees'],
  'edit_employee_phone': ['view_employees'],
  'edit_employee_hire_date': ['view_employees'],
  'edit_employee_fire_date': ['view_employees'],
  'view_employment_history': ['view_employees'],
  'create_employment_periods': ['view_employment_history'],
  'edit_employment_history': ['view_employment_history'],
  'delete_employment_history': ['view_employment_history'],
  'edit_employee_status': ['view_employees'],
  'edit_employee_locations': ['view_employees'],
  'delete_employees': ['view_employees'],
  'edit_notes': ['view_notes'],
  'delete_notes': ['view_notes'],
  'edit_disciplinary_actions': ['view_disciplinary_actions'],
  'delete_disciplinary_actions': ['view_disciplinary_actions'],
  'view_role_tip_eligibility': ['view_roles'],
  'create_roles': ['view_roles'],
  'edit_roles': ['view_roles'],
  'edit_role_tip_eligibility': ['view_role_tip_eligibility'],
  'assign_roles': ['view_roles'],
  'remove_roles': ['view_roles'],
  'import_square': ['view_square'],
  'create_timecards': ['view_timecards'],
  'edit_timecards': ['view_timecards'],
  'delete_timecards': ['view_timecards'],
  'view_estimated_tips': ['view_timecards'],
  'edit_timecards_as_shift_lead': ['view_timecards_as_shift_lead'],
  'edit_shift_lead_day_marks': ['view_shift_lead_day_marks'],
  'view_settings_timecards': ['view_settings'],
  'edit_settings_timecards': ['view_settings_timecards'],
  'view_settings_audit': ['view_settings'],
  'edit_settings_audit': ['view_settings_audit'],
  'view_settings_time_off': ['view_settings'],
  'edit_settings_time_off': ['view_settings_time_off'],
  'view_settings_invoices': ['view_settings'],
  'edit_settings_invoices': ['view_settings_invoices'],
  'view_settings_schedules': ['view_settings'],
  'edit_settings_schedules': ['view_settings_schedules'],
  'view_settings_roles': ['view_settings'],
  'edit_settings_roles': ['view_settings_roles'],
  'view_settings_square': ['view_settings'],
  'edit_settings_square': ['view_settings_square'],
  'view_settings_updates': ['view_settings'],
  'edit_settings_updates': ['view_settings_updates'],
  'view_time_off_recorded_by': ['view_time_off'],
  'view_time_off_added_date': ['view_time_off'],
  'create_time_off': ['view_time_off'],
  'edit_time_off': ['view_time_off'],
  'delete_time_off': ['view_time_off'],
  'approve_time_off_requests': ['view_time_off_requests'],
  'deny_time_off_requests': ['view_time_off_requests'],
  'upload_documents': ['view_documents'],
  'edit_documents': ['view_documents'],
  'delete_documents': ['view_documents'],
  'upload_employee_file_application': ['view_employee_file_application'],
  'edit_employee_file_application': ['view_employee_file_application'],
  'delete_employee_file_application': ['view_employee_file_application'],
  'upload_employee_file_performance': ['view_employee_file_performance'],
  'edit_employee_file_performance': ['view_employee_file_performance'],
  'delete_employee_file_performance': ['view_employee_file_performance'],
  'upload_employee_file_leave': ['view_employee_file_leave'],
  'edit_employee_file_leave': ['view_employee_file_leave'],
  'delete_employee_file_leave': ['view_employee_file_leave'],
  'upload_employee_file_injury': ['view_employee_file_injury'],
  'edit_employee_file_injury': ['view_employee_file_injury'],
  'delete_employee_file_injury': ['view_employee_file_injury'],
  'upload_employee_file_payroll': ['view_employee_file_payroll'],
  'edit_employee_file_payroll': ['view_employee_file_payroll'],
  'delete_employee_file_payroll': ['view_employee_file_payroll'],
  'upload_employee_file_requests': ['view_employee_file_requests'],
  'edit_employee_file_requests': ['view_employee_file_requests'],
  'delete_employee_file_requests': ['view_employee_file_requests'],
  'upload_employee_file_agreement': ['view_employee_file_agreement'],
  'edit_employee_file_agreement': ['view_employee_file_agreement'],
  'delete_employee_file_agreement': ['view_employee_file_agreement'],
  'edit_schedule_prefs': ['view_schedule_prefs'],
  'manage_inventory': ['view_inventory'],
  'grant_any': ['grant_permissions'],
  'manage_employee_links': ['view_users'],
  'archive_users': ['view_users'],
};

export function parseLocationSchedulePermission(permissionName) {
  if (!permissionName || typeof permissionName !== 'string') return null;
  const match = permissionName.match(
    /^(view|export_text|export|sync|create|auto|edit|delete)_schedules__(.+)$/
  );
  if (!match) return null;
  return { action: match[1], locationId: match[2] };
}

export function locationSchedulePermissionName(action, locationId) {
  return `${action}_schedules__${String(locationId)}`;
}

export function parseDemandLocationPermission(permissionName) {
  if (!permissionName || typeof permissionName !== 'string') return null;
  const match = permissionName.match(/^(view|edit)_schedule_demand__(.+)$/);
  if (!match) return null;
  return { action: match[1], locationId: match[2] };
}

export function demandLocationPermissionName(action, locationId) {
  return `${action}_schedule_demand__${String(locationId)}`;
}

export function parseReportLocationPermission(permissionName) {
  if (!permissionName || typeof permissionName !== 'string') return null;
  const match = permissionName.match(/^view_report_location__(.+)$/);
  if (!match) return null;
  return { locationId: match[1] };
}

export function reportLocationPermissionName(locationId) {
  return `view_report_location__${String(locationId)}`;
}

export function parseItemLocationPermission(permissionName) {
  if (!permissionName || typeof permissionName !== 'string') return null;
  const match = permissionName.match(
    /^(view|sync|create|edit|availability|archive|delete)_items__(.+)$/
  );
  if (!match) return null;
  return { action: match[1], locationId: match[2] };
}

export function itemLocationPermissionName(action, locationId) {
  return `${action}_items__${String(locationId)}`;
}

export function parseModifierLocationPermission(permissionName) {
  if (!permissionName || typeof permissionName !== 'string') return null;
  const match = permissionName.match(
    /^(view|sync|create|edit|delete)_modifiers__(.+)$/
  );
  if (!match) return null;
  return { action: match[1], locationId: match[2] };
}

export function modifierLocationPermissionName(action, locationId) {
  return `${action}_modifiers__${String(locationId)}`;
}

export function parseInvoiceLocationPermission(permissionName) {
  if (!permissionName || typeof permissionName !== 'string') return null;
  const match = permissionName.match(
    /^(view_draft|view_unpaid|view_paid|view_canceled|view_overdue|view|create|manage|tips|schedule|payment_methods|discounts)_invoices__(.+)$/
  );
  if (!match) return null;
  return { action: match[1], locationId: match[2] };
}

export function invoiceLocationPermissionName(action, locationId) {
  return `${action}_invoices__${String(locationId)}`;
}

export function parseGiftCardLocationPermission(permissionName) {
  if (!permissionName || typeof permissionName !== 'string') return null;
  const match = permissionName.match(
    /^(view|create|activate|load|redeem|clear|adjust|link|block)_gift_cards__(.+)$/
  );
  if (!match) return null;
  return { action: match[1], locationId: match[2] };
}

export function giftCardLocationPermissionName(action, locationId) {
  return `${action}_gift_cards__${String(locationId)}`;
}

const INVOICE_UNPAID_STATUSES = new Set([
  'UNPAID',
  'PARTIALLY_PAID',
  'PAYMENT_PENDING',
  'SCHEDULED',
]);

const INVOICE_CANCELED_STATUSES = new Set(['CANCELED', 'FAILED']);

/** Map Square invoice status → status-view permission action. */
export function invoiceStatusViewAction(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'DRAFT') return 'view_draft';
  if (s === 'PAID') return 'view_paid';
  if (INVOICE_CANCELED_STATUSES.has(s)) return 'view_canceled';
  if (INVOICE_UNPAID_STATUSES.has(s)) return 'view_unpaid';
  return 'view_unpaid';
}

function overdueCutoffYmd(todayYmd) {
  const raw = String(todayYmd || '').trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  let year = Number(match[1]);
  let month = Number(match[2]);
  const day = Number(match[3]);
  month -= 1;
  if (month < 1) {
    month = 12;
    year -= 1;
  }
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const clampedDay = Math.min(day, daysInMonth);
  return `${year}-${String(month).padStart(2, '0')}-${String(clampedDay).padStart(2, '0')}`;
}

/** True when due date is more than one calendar month before todayYmd (YYYY-MM-DD). */
export function isInvoiceDueOverdue(dueDate, todayYmd) {
  const due = String(dueDate || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) return false;
  const cutoff = overdueCutoffYmd(todayYmd);
  if (!cutoff) return false;
  return due < cutoff;
}

/**
 * Whether the user may see this invoice (base view + status + overdue gates).
 * `hasInvoicePermission(action, locationId)` should already treat superuser as allowed.
 */
export function canViewInvoiceRecord(invoice, hasInvoicePermission, todayYmd) {
  const locationId = invoice?.locationId || invoice?.location_id;
  if (!hasInvoicePermission('view', locationId)) return false;
  const statusAction = invoiceStatusViewAction(invoice?.status);
  if (!hasInvoicePermission(statusAction, locationId)) return false;
  if (
    isInvoiceDueOverdue(invoice?.dueDate || invoice?.due_date, todayYmd) &&
    !hasInvoicePermission('view_overdue', locationId)
  ) {
    return false;
  }
  return true;
}

export const isAnyInvoicePermission = (permissionName, action = null) => {
  const parsed = parseInvoiceLocationPermission(permissionName);
  if (!parsed) return false;
  return action == null || parsed.action === action;
};

export const isAnyGiftCardPermission = (permissionName, action = null) => {
  const parsed = parseGiftCardLocationPermission(permissionName);
  if (!parsed) return false;
  return action == null || parsed.action === action;
};

export const isAnyItemPermission = (permissionName, action = null) => {
  const parsed = parseItemLocationPermission(permissionName);
  if (!parsed) return false;
  return action == null || parsed.action === action;
};

export const isAnyModifierPermission = (permissionName, action = null) => {
  const parsed = parseModifierLocationPermission(permissionName);
  if (!parsed) return false;
  return action == null || parsed.action === action;
};

export const getPermissionPrerequisites = (permissionName) => {
  if (PERMISSION_PREREQUISITES[permissionName]) {
    return PERMISSION_PREREQUISITES[permissionName];
  }
  const itemParsed = parseItemLocationPermission(permissionName);
  if (itemParsed) {
    if (itemParsed.action === 'view') return [];
    return [itemLocationPermissionName('view', itemParsed.locationId)];
  }
  const modifierParsed = parseModifierLocationPermission(permissionName);
  if (modifierParsed) {
    if (modifierParsed.action === 'view') return [];
    return [modifierLocationPermissionName('view', modifierParsed.locationId)];
  }
  const invoiceParsed = parseInvoiceLocationPermission(permissionName);
  if (invoiceParsed) {
    if (invoiceParsed.action === 'view') return [];
    if (
      ['tips', 'schedule', 'payment_methods', 'discounts'].includes(
        invoiceParsed.action
      )
    ) {
      return [invoiceLocationPermissionName('create', invoiceParsed.locationId)];
    }
    return [invoiceLocationPermissionName('view', invoiceParsed.locationId)];
  }
  const giftCardParsed = parseGiftCardLocationPermission(permissionName);
  if (giftCardParsed) {
    if (giftCardParsed.action === 'view') return [];
    return [giftCardLocationPermissionName('view', giftCardParsed.locationId)];
  }
  const demandParsed = parseDemandLocationPermission(permissionName);
  if (demandParsed) {
    if (demandParsed.action === 'view') return ['view_roles'];
    return [demandLocationPermissionName('view', demandParsed.locationId)];
  }
  const parsed = parseLocationSchedulePermission(permissionName);
  if (!parsed) return [];
  if (parsed.action === 'view') {
    return ['view_employees', 'view_roles'];
  }
  if (parsed.action === 'auto') {
    return [locationSchedulePermissionName('create', parsed.locationId)];
  }
  return [locationSchedulePermissionName('view', parsed.locationId)];
};

/** True if the user has any location-scoped schedule permission for `action` (or any action). */
export const isAnySchedulePermission = (permissionName, action = null) => {
  const parsed = parseLocationSchedulePermission(permissionName);
  if (!parsed) return false;
  return action == null || parsed.action === action;
};

export const isAnyDemandPermission = (permissionName, action = null) => {
  const parsed = parseDemandLocationPermission(permissionName);
  if (!parsed) return false;
  return action == null || parsed.action === action;
};

/** Transitive closure of prerequisites for a permission (direct + nested). */
export const getAllPermissionPrerequisites = (permissionName) => {
  const all = new Set();
  const stack = [...getPermissionPrerequisites(permissionName)];
  while (stack.length > 0) {
    const prereq = stack.pop();
    if (!prereq || all.has(prereq)) continue;
    all.add(prereq);
    getPermissionPrerequisites(prereq).forEach((nested) => {
      if (!all.has(nested)) stack.push(nested);
    });
  }
  return Array.from(all);
};

export const getRequiredPermissions = (permissionName, allPermissionNames = []) => {
  // Get permissions that require this one as a prerequisite
  const required = new Set();
  Object.entries(PERMISSION_PREREQUISITES).forEach(([perm, prereqs]) => {
    if (prereqs.includes(permissionName)) {
      required.add(perm);
    }
  });

  const parsed = parseLocationSchedulePermission(permissionName);
  if (parsed?.action === 'view') {
    ['export', 'export_text', 'sync', 'create', 'auto', 'edit', 'delete'].forEach((action) => {
      required.add(locationSchedulePermissionName(action, parsed.locationId));
    });
  }
  if (parsed?.action === 'create') {
    required.add(locationSchedulePermissionName('auto', parsed.locationId));
  }

  const demandParsed = parseDemandLocationPermission(permissionName);
  if (demandParsed?.action === 'view') {
    required.add(demandLocationPermissionName('edit', demandParsed.locationId));
  }

  const itemParsed = parseItemLocationPermission(permissionName);
  if (itemParsed?.action === 'view') {
    ['sync', 'create', 'edit', 'availability', 'archive', 'delete'].forEach((action) => {
      required.add(itemLocationPermissionName(action, itemParsed.locationId));
    });
  }

  const modifierParsed = parseModifierLocationPermission(permissionName);
  if (modifierParsed?.action === 'view') {
    ['sync', 'create', 'edit', 'delete'].forEach((action) => {
      required.add(modifierLocationPermissionName(action, modifierParsed.locationId));
    });
  }

  const invoiceParsed = parseInvoiceLocationPermission(permissionName);
  if (invoiceParsed?.action === 'view') {
    [
      'view_draft',
      'view_unpaid',
      'view_paid',
      'view_canceled',
      'view_overdue',
      'create',
      'manage',
      'tips',
      'schedule',
      'payment_methods',
      'discounts',
    ].forEach((action) => {
      required.add(
        invoiceLocationPermissionName(action, invoiceParsed.locationId)
      );
    });
  }
  if (invoiceParsed?.action === 'create') {
    ['tips', 'schedule', 'payment_methods', 'discounts'].forEach((action) => {
      required.add(
        invoiceLocationPermissionName(action, invoiceParsed.locationId)
      );
    });
  }

  const giftCardParsed = parseGiftCardLocationPermission(permissionName);
  if (giftCardParsed?.action === 'view') {
    ['create', 'activate', 'load', 'redeem', 'clear', 'adjust', 'link', 'block'].forEach(
      (action) => {
        required.add(
          giftCardLocationPermissionName(action, giftCardParsed.locationId)
        );
      }
    );
  }

  (allPermissionNames || []).forEach((name) => {
    if (name !== permissionName && getPermissionPrerequisites(name).includes(permissionName)) {
      required.add(name);
    }
  });

  return Array.from(required);
};

/**
 * Expand a permission list to include static + location-scoped prerequisites.
 * Location dependents are resolved from the granted set itself.
 */
export const expandPermissionsList = (permissions) => {
  const expanded = new Set(permissions);

  let changed = true;
  while (changed) {
    changed = false;
    Array.from(expanded).forEach((perm) => {
      getPermissionPrerequisites(perm).forEach((prereq) => {
        if (!expanded.has(prereq)) {
          expanded.add(prereq);
          changed = true;
        }
      });
    });
  }

  return Array.from(expanded);
};

const permissionsKey = (permissions) =>
  Array.isArray(permissions) ? [...permissions].sort().join('|') : '';

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  return context || {
    hasPermission: () => false,
    hasAnyPermission: () => false,
    hasAllPermissions: () => false,
    hasSchedulePermission: () => false,
    hasAnySchedulePermission: () => false,
    scheduleLocationIds: () => [],
    hasDemandPermission: () => false,
    hasAnyDemandPermission: () => false,
    demandLocationIds: () => [],
    hasReportLocationPermission: () => false,
    hasAnyReportLocationPermission: () => false,
    reportLocationIds: () => [],
    canViewAllReportLocations: () => false,
    hasItemPermission: () => false,
    hasAnyItemPermission: () => false,
    itemLocationIds: () => [],
    hasModifierPermission: () => false,
    hasAnyModifierPermission: () => false,
    modifierLocationIds: () => [],
    hasInvoicePermission: () => false,
    hasAnyInvoicePermission: () => false,
    invoiceLocationIds: () => [],
    hasGiftCardPermission: () => false,
    hasAnyGiftCardPermission: () => false,
      giftCardLocationIds: () => [],
    permissions: [],
    rawPermissions: []
  };
};

export const PermissionsProvider = ({ children, permissions = [] }) => {
  const key = permissionsKey(permissions);
  const rawPermissions = useMemo(
    () => (key ? key.split('|') : []),
    [key]
  );

  const expandedPermissions = useMemo(
    () => expandPermissionsList(rawPermissions),
    [rawPermissions]
  );

  const isSuperuser = expandedPermissions.includes('superuser');

  const hasPermission = useCallback(
    (permissionName) => isSuperuser || expandedPermissions.includes(permissionName),
    [expandedPermissions, isSuperuser]
  );

  const hasAnyPermission = useCallback(
    (permissionNames) =>
      isSuperuser || permissionNames.some((name) => expandedPermissions.includes(name)),
    [expandedPermissions, isSuperuser]
  );

  const hasAllPermissions = useCallback(
    (permissionNames) =>
      isSuperuser || permissionNames.every((name) => expandedPermissions.includes(name)),
    [expandedPermissions, isSuperuser]
  );

  const hasAnySchedulePermission = useCallback(
    (action = null) => {
      if (isSuperuser) return true;
      return expandedPermissions.some((name) => isAnySchedulePermission(name, action));
    },
    [expandedPermissions, isSuperuser]
  );

  const hasSchedulePermission = useCallback(
    (action, locationId) => {
      if (isSuperuser) return true;
      if (!locationId) {
        return hasAnySchedulePermission(action);
      }
      return expandedPermissions.includes(locationSchedulePermissionName(action, locationId));
    },
    [expandedPermissions, isSuperuser, hasAnySchedulePermission]
  );

  const scheduleLocationIds = useCallback(
    (action, allLocationIds = []) => {
      if (isSuperuser) {
        return allLocationIds.map(String);
      }
      const scoped = expandedPermissions
        .map(parseLocationSchedulePermission)
        .filter((p) => p && p.action === action)
        .map((p) => p.locationId);
      if (!allLocationIds.length) return scoped;
      const allowed = new Set(allLocationIds.map(String));
      return scoped.filter((id) => allowed.has(String(id)));
    },
    [expandedPermissions, isSuperuser]
  );

  const hasLegacyGlobalDemand = useCallback(
    (action = null) => {
      // Temporary until location sync migrates/drops leftover global grants.
      if (action == null || action === 'view') {
        if (
          expandedPermissions.includes('view_schedule_demand') ||
          expandedPermissions.includes('edit_schedule_demand')
        ) {
          return true;
        }
      }
      return action === 'edit' && expandedPermissions.includes('edit_schedule_demand');
    },
    [expandedPermissions]
  );

  const hasAnyDemandPermission = useCallback(
    (action = null) => {
      if (isSuperuser) return true;
      if (hasLegacyGlobalDemand(action)) return true;
      return expandedPermissions.some((name) => isAnyDemandPermission(name, action));
    },
    [expandedPermissions, isSuperuser, hasLegacyGlobalDemand]
  );

  const hasDemandPermission = useCallback(
    (action, locationId) => {
      if (isSuperuser) return true;
      if (hasLegacyGlobalDemand(action)) return true;
      if (!locationId) {
        return hasAnyDemandPermission(action);
      }
      return expandedPermissions.includes(demandLocationPermissionName(action, locationId));
    },
    [expandedPermissions, isSuperuser, hasAnyDemandPermission, hasLegacyGlobalDemand]
  );

  const demandLocationIds = useCallback(
    (action, allLocationIds = []) => {
      if (isSuperuser || hasLegacyGlobalDemand(action)) {
        return allLocationIds.map(String);
      }
      const scoped = expandedPermissions
        .map(parseDemandLocationPermission)
        .filter((p) => p && p.action === action)
        .map((p) => p.locationId);
      if (!allLocationIds.length) return scoped;
      const allowed = new Set(allLocationIds.map(String));
      return scoped.filter((id) => allowed.has(String(id)));
    },
    [expandedPermissions, isSuperuser, hasLegacyGlobalDemand]
  );

  const hasReportLocationPermission = useCallback(
    (locationId) => {
      if (isSuperuser) return true;
      if (!locationId) {
        return expandedPermissions.some((name) => !!parseReportLocationPermission(name));
      }
      return expandedPermissions.includes(reportLocationPermissionName(locationId));
    },
    [expandedPermissions, isSuperuser]
  );

  const hasAnyReportLocationPermission = useCallback(
    () => hasReportLocationPermission(null),
    [hasReportLocationPermission]
  );

  const reportLocationIds = useCallback(
    (allLocationIds = []) => {
      if (isSuperuser) {
        return allLocationIds.map(String);
      }
      const scoped = expandedPermissions
        .map(parseReportLocationPermission)
        .filter(Boolean)
        .map((p) => p.locationId);
      if (!allLocationIds.length) return scoped;
      const allowed = new Set(allLocationIds.map(String));
      return scoped.filter((id) => allowed.has(String(id)));
    },
    [expandedPermissions, isSuperuser]
  );

  const canViewAllReportLocations = useCallback(
    (allLocationIds = []) => {
      if (isSuperuser) return true;
      const ids = (allLocationIds || []).map(String).filter(Boolean);
      if (!ids.length) return false;
      return ids.every((id) =>
        expandedPermissions.includes(reportLocationPermissionName(id))
      );
    },
    [expandedPermissions, isSuperuser]
  );

  const hasAnyItemPermission = useCallback(
    (action = null) => {
      if (isSuperuser) return true;
      return expandedPermissions.some((name) => isAnyItemPermission(name, action));
    },
    [expandedPermissions, isSuperuser]
  );

  const hasItemPermission = useCallback(
    (action, locationId) => {
      if (isSuperuser) return true;
      if (!locationId) {
        return hasAnyItemPermission(action);
      }
      return expandedPermissions.includes(itemLocationPermissionName(action, locationId));
    },
    [expandedPermissions, isSuperuser, hasAnyItemPermission]
  );

  const itemLocationIds = useCallback(
    (action, allLocationIds = []) => {
      if (isSuperuser) {
        return allLocationIds.map(String);
      }
      const scoped = expandedPermissions
        .map(parseItemLocationPermission)
        .filter((p) => p && p.action === action)
        .map((p) => p.locationId);
      if (!allLocationIds.length) return scoped;
      const allowed = new Set(allLocationIds.map(String));
      return scoped.filter((id) => allowed.has(String(id)));
    },
    [expandedPermissions, isSuperuser]
  );

  const hasAnyModifierPermission = useCallback(
    (action = null) => {
      if (isSuperuser) return true;
      return expandedPermissions.some((name) =>
        isAnyModifierPermission(name, action)
      );
    },
    [expandedPermissions, isSuperuser]
  );

  const hasModifierPermission = useCallback(
    (action, locationId) => {
      if (isSuperuser) return true;
      if (!locationId) {
        return hasAnyModifierPermission(action);
      }
      return expandedPermissions.includes(
        modifierLocationPermissionName(action, locationId)
      );
    },
    [expandedPermissions, isSuperuser, hasAnyModifierPermission]
  );

  const modifierLocationIds = useCallback(
    (action, allLocationIds = []) => {
      if (isSuperuser) {
        return allLocationIds.map(String);
      }
      const scoped = expandedPermissions
        .map(parseModifierLocationPermission)
        .filter((p) => p && p.action === action)
        .map((p) => p.locationId);
      if (!allLocationIds.length) return scoped;
      const allowed = new Set(allLocationIds.map(String));
      return scoped.filter((id) => allowed.has(String(id)));
    },
    [expandedPermissions, isSuperuser]
  );

  const hasAnyInvoicePermission = useCallback(
    (action = null) => {
      if (isSuperuser) return true;
      return expandedPermissions.some((name) => isAnyInvoicePermission(name, action));
    },
    [expandedPermissions, isSuperuser]
  );

  const hasInvoicePermission = useCallback(
    (action, locationId) => {
      if (isSuperuser) return true;
      if (!locationId) {
        return hasAnyInvoicePermission(action);
      }
      return expandedPermissions.includes(
        invoiceLocationPermissionName(action, locationId)
      );
    },
    [expandedPermissions, isSuperuser, hasAnyInvoicePermission]
  );

  const invoiceLocationIds = useCallback(
    (action, allLocationIds = []) => {
      if (isSuperuser) {
        return allLocationIds.map(String);
      }
      const scoped = expandedPermissions
        .map(parseInvoiceLocationPermission)
        .filter((p) => p && p.action === action)
        .map((p) => p.locationId);
      if (!allLocationIds.length) return scoped;
      const allowed = new Set(allLocationIds.map(String));
      return scoped.filter((id) => allowed.has(String(id)));
    },
    [expandedPermissions, isSuperuser]
  );

  const hasAnyGiftCardPermission = useCallback(
    (action = null) => {
      if (isSuperuser) return true;
      return expandedPermissions.some((name) =>
        isAnyGiftCardPermission(name, action)
      );
    },
    [expandedPermissions, isSuperuser]
  );

  const hasGiftCardPermission = useCallback(
    (action, locationId) => {
      if (isSuperuser) return true;
      if (!locationId) {
        return hasAnyGiftCardPermission(action);
      }
      return expandedPermissions.includes(
        giftCardLocationPermissionName(action, locationId)
      );
    },
    [expandedPermissions, isSuperuser, hasAnyGiftCardPermission]
  );

  const giftCardLocationIds = useCallback(
    (action, allLocationIds = []) => {
      if (isSuperuser) {
        return allLocationIds.map(String);
      }
      const scoped = expandedPermissions
        .map(parseGiftCardLocationPermission)
        .filter((p) => p && p.action === action)
        .map((p) => p.locationId);
      if (!allLocationIds.length) return scoped;
      const allowed = new Set(allLocationIds.map(String));
      return scoped.filter((id) => allowed.has(String(id)));
    },
    [expandedPermissions, isSuperuser]
  );

  const value = useMemo(
    () => ({
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      hasSchedulePermission,
      hasAnySchedulePermission,
      scheduleLocationIds,
      hasDemandPermission,
      hasAnyDemandPermission,
      demandLocationIds,
      hasReportLocationPermission,
      hasAnyReportLocationPermission,
      reportLocationIds,
      canViewAllReportLocations,
      hasItemPermission,
      hasAnyItemPermission,
      itemLocationIds,
      hasModifierPermission,
      hasAnyModifierPermission,
      modifierLocationIds,
      hasInvoicePermission,
      hasAnyInvoicePermission,
      invoiceLocationIds,
      hasGiftCardPermission,
      hasAnyGiftCardPermission,
      giftCardLocationIds,
      permissions: expandedPermissions,
      rawPermissions
    }),
    [
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      hasSchedulePermission,
      hasAnySchedulePermission,
      scheduleLocationIds,
      hasDemandPermission,
      hasAnyDemandPermission,
      demandLocationIds,
      hasReportLocationPermission,
      hasAnyReportLocationPermission,
      reportLocationIds,
      canViewAllReportLocations,
      hasItemPermission,
      hasAnyItemPermission,
      itemLocationIds,
      hasModifierPermission,
      hasAnyModifierPermission,
      modifierLocationIds,
      hasInvoicePermission,
      hasAnyInvoicePermission,
      invoiceLocationIds,
      hasGiftCardPermission,
      hasAnyGiftCardPermission,
      giftCardLocationIds,
      expandedPermissions,
      rawPermissions
    ]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
